import { Category } from '@/class/Category';
import { HTTPResponse } from '@/class/HTTPResponse';
import { fetchData, postData, putData } from '../../../lib/api';
import SystemAdminLayout from '../../layout/SystemAdminLayout';
import CategoryCreateModal from './createModal';
import CategoryEditModal from './editModal';
import { CategoryForm, emptyCategoryForm, parseCategoryForm } from './categoryForm';
import {
  adminCard,
  adminSectionTitle,
  adminTableCell,
  adminTableHead,
  adminButtonPrimary,
  adminPageToolbar,
  adminTableWrap,
  adminTable,
} from '../../layout/adminStyles';
import React, { useCallback, useEffect, useState } from 'react';

const categoryToForm = (category: Category): CategoryForm => ({
  name: category.name ?? '',
  display_order: category.display_order != null ? String(category.display_order) : '',
});

const SystemAdminCategoryIndexPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);
  const [isShowCreateModal, setIsShowCreateModal] = useState<boolean>(false);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<CategoryForm>(emptyCategoryForm());
  const [createForm, setCreateForm] = useState<CategoryForm>(emptyCategoryForm());
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const loadCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const res: HTTPResponse = await fetchData('category');
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      console.error(err);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const openEditModal = (category: Category) => {
    setEditingCategoryId(category.id);
    setEditForm(categoryToForm(category));
    setIsShowEditModal(true);
  };

  const closeEditModal = () => {
    setIsShowEditModal(false);
    setEditingCategoryId(null);
    setEditForm(emptyCategoryForm());
  };

  const openCreateModal = () => {
    const maxDisplayOrder = categories.reduce(
      (max, category) => Math.max(max, category.display_order ?? 0),
      0
    );
    setCreateForm({
      ...emptyCategoryForm(),
      display_order: String(maxDisplayOrder + 1),
    });
    setIsShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setIsShowCreateModal(false);
    setCreateForm(emptyCategoryForm());
  };

  const handleSave = async () => {
    if (editingCategoryId == null) {
      return;
    }

    const payload = parseCategoryForm(editForm);
    if (!payload) {
      return;
    }

    setIsSaving(true);
    try {
      const res: HTTPResponse = await putData(`category/${editingCategoryId}`, payload);

      if (res.data) {
        const updated = res.data as Category;
        setCategories((prev) =>
          prev
            .map((category) => (category.id === editingCategoryId ? updated : category))
            .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        );
        closeEditModal();
      }
    } catch (err: unknown) {
      console.error(err);
      alert('カテゴリの更新に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    const payload = parseCategoryForm(createForm);
    if (!payload) {
      return;
    }

    setIsSaving(true);
    try {
      const res: HTTPResponse = await postData('category', payload);

      if (res.data) {
        const created = res.data as Category;
        setCategories((prev) =>
          [...prev, created].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        );
        closeCreateModal();
      }
    } catch (err: unknown) {
      console.error(err);
      alert('カテゴリの登録に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SystemAdminLayout>
      <div className={adminCard}>
        <div className={adminPageToolbar}>
          <h2 className={adminSectionTitle + ' mb-0 border-0 pb-0'}>カテゴリ一覧</h2>
          <button type="button" className={`${adminButtonPrimary} w-full sm:w-auto`} onClick={openCreateModal}>
            新規登録
          </button>
        </div>
        <p className="text-sm text-stone-600 mb-4">全 {categories.length} 件</p>

        <div className={adminTableWrap}>
        <table className={adminTable}>
          <thead>
            <tr className={adminTableHead}>
              <th className={`${adminTableCell} text-left w-16`}>ID</th>
              <th className={`${adminTableCell} text-left`}>カテゴリ名</th>
              <th className={`${adminTableCell} text-center w-28`}>表示順</th>
              <th className={`${adminTableCell} text-center w-24`}>操作</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className={`${adminTableCell} p-6 text-center text-stone-500`}>
                  読み込み中...
                </td>
              </tr>
            ) : categories.length === 0 ? (
              <tr>
                <td colSpan={6} className={`${adminTableCell} p-6 text-center text-stone-500`}>
                  カテゴリがありません。
                </td>
              </tr>
            ) : (
              categories.map((category: Category) => (
                <tr key={category.id}>
                  <td className={adminTableCell}>{category.id}</td>
                  <td className={adminTableCell}>{category.name}</td>
                  <td className={`${adminTableCell} text-center`}>{category.display_order ?? '-'}</td>
                  <td className={`${adminTableCell} text-center`}>
                    <button
                      type="button"
                      className={`${adminButtonPrimary} text-sm py-1 px-3`}
                      onClick={() => openEditModal(category)}
                    >
                      編集
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <CategoryEditModal
        isShowModal={isShowEditModal}
        categoryId={editingCategoryId}
        form={editForm}
        isSaving={isSaving}
        onChange={setEditForm}
        onClose={closeEditModal}
        onSubmit={handleSave}
      />

      <CategoryCreateModal
        isShowModal={isShowCreateModal}
        form={createForm}
        isSaving={isSaving}
        onChange={setCreateForm}
        onClose={closeCreateModal}
        onSubmit={handleCreate}
      />
    </SystemAdminLayout>
  );
};

export default SystemAdminCategoryIndexPage;
